<template>
  <div :class="$style.previewWrapper">
    <div v-if="isOverlay" :class="$style.overlayBackground" @click="onClose?.()"></div>
    <div v-if="isOverlay" :class="$style.closeButtonContainer">
      <SCAIconButton
        :class="$style.closeButton"
        appearance="inverted"
        type="ghost"
        size="xsmall"
        icon="phosphor/x"
        icon-size="var(--size-xSmall)"
        @click="onClose?.()"
      />
    </div>
    <div :class="[$style.previewContainer, isOverlay && $style.overlayMode]" @click.stop>
      <EmptyState v-if="shownError" :message="shownError" :class="$style.empty" test-id="file-preview-error" />
      <FilePreviewSkeleton v-else-if="loading" />
      <template v-else-if="layers.length">
        <div v-for="layer in layers" :key="layer.url" :class="[$style.layer, isHidden(layer) && $style.hiddenLayer]">
          <ImagePreview
            v-if="isImage(layer.type)"
            :file-url="layer.url"
            :file-name="layer.name"
            :on-close="onClose"
            @content-click="onImageClick"
            @loaded="onLoaded(layer)"
            @error="onFailed(layer)"
          />
          <PdfPreview
            v-else
            :file-url="layer.url"
            :on-close="onClose"
            :expand-on-click="canExpand"
            @content-click="emit('expand')"
            @loaded="onLoaded(layer)"
          />
        </div>
      </template>
      <EmptyState
        v-else
        :message="fileUrl ? $t('common.label.unsupportedFileType') : $t('common.label.filesPreview')"
        :class="$style.empty"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { SCAIconButton } from "@scayla/components";
import { useEventListener } from "@vueuse/core";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { isImage } from "@/utils/fileAttachments";

import { usePreviewLayers } from "./composables/usePreviewLayers";
import EmptyState from "./FilePreviewEmptyState.vue";
import FilePreviewSkeleton from "./FilePreviewSkeleton.vue";
import ImagePreview from "./ImagePreview.vue";
import PdfPreview from "./PdfPreview.vue";

const { t: $t } = useI18n();

const props = withDefaults(
  defineProps<{
    fileUrl?: string;
    fileType?: string;
    fileName?: string;
    isOverlay: boolean;
    /** The file or its URL is still on its way; the image and PDF show their own skeleton while they download. */
    loading?: boolean;
    /** Shown in place of the file, e.g. when it no longer exists. */
    errorMessage?: string;
    /** Closes the preview; without it there is no close button, as on the standalone preview page. */
    onClose?: () => void;
    /** A click on the content emits `expand`, unless the preview is already the overlay. */
    expandable?: boolean;
  }>(),
  {
    fileUrl: undefined,
    fileType: undefined,
    fileName: undefined,
    loading: false,
    errorMessage: "",
    onClose: undefined,
    expandable: false,
  },
);

const emit = defineEmits<{ expand: [] }>();

const canExpand = computed(() => props.expandable && !props.isOverlay);

const { layers, hasFailed, isHidden, onLoaded, onFailed } = usePreviewLayers({
  fileUrl: () => props.fileUrl,
  fileType: () => props.fileType,
  fileName: () => props.fileName,
});

const shownError = computed(() => props.errorMessage || (hasFailed.value ? $t("common.message.fileLoadError") : ""));

function onImageClick() {
  if (props.isOverlay) props.onClose?.();
  else if (canExpand.value) emit("expand");
}

useEventListener(window, "keydown", (event: KeyboardEvent) => {
  if (props.isOverlay && event.key === "Escape") props.onClose?.();
});
</script>

<style lang="scss" module>
.previewWrapper {
  all: unset;
  display: contents;
}
.previewContainer {
  position: relative;
  z-index: 1;
  overflow: auto;
  height: 100%;
}

.overlayBackground {
  position: fixed;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.5);
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.overlayMode {
  position: fixed;
  z-index: 1001;
  top: 0;
  left: 5vw;
  right: 5vw;
  bottom: 0;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    left: 6vw;
    right: 6vw;
  }

  @media (max-width: 426px) {
    top: 4vh;
    left: 4vw;
    right: 4vw;
    bottom: 4vh;
    height: 94%;
  }
}

.closeButton {
  aspect-ratio: 1/1;
}

.closeButtonContainer {
  position: fixed;
  z-index: 1002;
  left: 5vw;
  top: var(--dimension-200);
  transform: translateX(calc(-100% - 22px));
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    transform: translateX(calc(-96%));
  }

  @media (max-width: 478px) {
    display: none;
  }
}

.layer {
  height: 100%;
}

// Loads the next file behind the one on screen; still laid out, so the image can size itself.
.hiddenLayer {
  position: absolute;
  inset: 0;
  visibility: hidden;
}

.empty {
  background: var(--color-background-default);
  text-align: center;
  padding: 0 var(--space-component-padding-small);
}
</style>
